/* AgentNavRail — what the agent becomes while it drives the console.

   When NoVA (CopilotPanel) or the public dock (AgentDock) navigates, the
   panel must not vanish: it slides right into this slim edge rail so the
   screen it just opened is uncovered, while the session stays visibly hot
   (orb + live voice state, in-rail mute / end) and one click brings the
   full panel back. A "Navigating → <Screen>" pill slides out of the rail
   for a few seconds so the jump is obviously the agent's doing.

   Pattern from Anastasia-dev VoiceAgent rail mode (commit 3c51ea7):
   right:0 strip, 0.25s slide-in, pulsing orb, state label, reduced-motion
   safe. Here the label is the agent's name, the banner names the screen. */
import React, { useEffect, useState } from 'react';
import { MODULES, SCREENS, moduleByPath } from '../data/consoleModules';
import './AgentNavRail.css';

const PUBLIC_LABELS = {
  '/': 'Home', '/pricing': 'Pricing', '/platform': 'Platform', '/app-kit': 'App Kit', '/store': 'Store',
  '/partners': 'Partners', '/contact': 'Contact', '/support': 'Support', '/docs': 'Docs', '/orgs': 'Organizations', '/jobs': 'Jobs',
};
const title = (s) => String(s || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Human label for a route the agent is opening — module label (+ tab),
// screen label, public page, or the path itself when nothing matches.
export function screenLabel(path) {
  let p = String(path || '').trim();
  const org = p.match(/^\/orgs\/[\w-]+\/?(.*)$/);
  if (org) p = org[1];
  else if (p.startsWith('/')) {
    const base = p.split('?')[0].replace(/\/$/, '') || '/';
    if (PUBLIC_LABELS[base]) return PUBLIC_LABELS[base];
    if (base.startsWith('/platform/')) return title(base.split('/')[2]);
    if (base.startsWith('/concepts/')) return `${title(base.split('/')[2])} (concept)`;
    return base;
  }
  const [base, query = ''] = p.split('?');
  const head = base.split('/')[0];
  if (!head) return 'Home';
  const screen = SCREENS.find((s) => s.to === head);
  if (screen) return screen.label;
  if (head === 'ws') return base.includes('/pipelines/') ? 'Workflow canvas' : 'Workspace';
  const mod = moduleByPath(head) || MODULES.find((m) => m.to === head);
  if (!mod) return p || 'Home';
  const tab = /(?:^|&)tab=([\w-]+)/.exec(query)?.[1];
  return tab ? `${mod.label} · ${title(tab)}` : mod.label;
}

const BANNER_MS = 3200;

export default function AgentNavRail({
  name = 'NoVA',
  nav = null,          // { label, at } — the latest navigation (new `at` re-shows the banner)
  live = false,        // voice session active
  stateLabel = '',     // Listening / Speaking / Thinking …
  clock = '',          // m:ss while live
  muted = false,
  onToggleMute,
  onEnd,
  onRestore,
}) {
  const [banner, setBanner] = useState(false);
  useEffect(() => {
    if (!nav?.at) return undefined;
    setBanner(true);
    const t = setTimeout(() => setBanner(false), BANNER_MS);
    return () => clearTimeout(t);
  }, [nav?.at]);

  return (
    <div className={`anr ${live ? 'anr-live' : ''}`} role="dialog" aria-label={`${name} (stepped aside while navigating)`}>
      {nav?.label && (
        <div className={`anr-banner ${banner ? 'anr-banner-in' : 'anr-banner-out'}`} aria-live="polite">
          <span className="anr-banner-kicker">{name} is navigating</span>
          <span className="anr-banner-to">→ {nav.label}</span>
          <span className="anr-banner-sweep" aria-hidden="true" />
        </div>
      )}
      <button type="button" className="anr-body" onClick={onRestore} title={`Re-open ${name}`}>
        <span className="anr-chevron" aria-hidden="true">‹</span>
        <span className={`anr-orb ${live ? 'anr-orb-live' : ''}`} aria-hidden="true" />
        <span className="anr-label">{live && stateLabel ? stateLabel : name}</span>
        {live && clock && <span className="anr-clock">{clock}</span>}
        <span className="anr-hint">tap to expand</span>
      </button>
      {live && (
        <div className="anr-controls">
          <button type="button" className={`anr-ctl ${muted ? 'anr-ctl-on' : ''}`} onClick={onToggleMute} aria-pressed={muted} title={muted ? 'Unmute microphone' : 'Mute microphone'}>
            {muted ? 'Unmute' : 'Mute'}
          </button>
          {onEnd && <button type="button" className="anr-ctl anr-ctl-end" onClick={onEnd} title="End the voice session">End</button>}
        </div>
      )}
    </div>
  );
}
